import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  Subject,
  take,
  firstValueFrom,
} from 'rxjs';
import { AuthService } from '@core/services/auth.service';
import { LoggingService } from '@core/services/logging.service';
import { ApiConfigurationService } from '@core/services/api-configuration.service';
import { CompanyService } from '@core/services/company.service';
import { PageContextService } from '@core/services/page-context.service';
import { RAGContext } from './chatbot-rag.models';

/**
 * Represents a single message in the chat, including user, bot, or system messages.
 *
 * @property id - Unique identifier for the message.
 * @property type - The type of message: 'user', 'bot', or 'system'.
 * @property content - The textual content of the message.
 * @property timestamp - The date and time when the message was created.
 * @property isStreaming - (Optional) Indicates if the message is currently streaming.
 * @property contextItems - (Optional) Number of context items associated with the message.
 */
export interface ChatMessage {
  id: string;
  type: 'user' | 'bot' | 'system';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  contextItems?: number;
}

/**
 * Represents a response from the chatbot streaming API.
 *
 * @property type - The type of stream event: 'start', 'incomplete', 'done', 'chunk', 'message', 'complete', or 'error'.
 * @property content - The content of the current stream chunk (used by 'chunk' or 'message' events).
 * @property partial_response - Partial response text during streaming (used by 'incomplete' events).
 * @property full_response - The complete response from the bot (used by 'complete' or 'done' events).
 * @property message - An optional message, typically used for errors or status updates.
 * @property context_items - The number of context items included in the response (scans, vulnerabilities, etc.).
 * @property chunks - The number of chunks processed so far in the stream.
 * @property request_id - Unique identifier for the streaming request.
 * @property deployment - Azure deployment name for the LLM.
 * @property api_version - API version being used for the request.
 * @property mode - Streaming mode indicator (e.g., 'streaming').
 * @property done - Boolean flag indicating stream completion (used by 'done' events).
 * @property code - (Optional) Error or status code from the API.
 * @property detail - (Optional) Additional error details from the API.
 * @property rag_context - (Optional) Truncated RAG context snapshot with lists limited to first 5 items.
 */
export interface StreamResponse {
  // Stream event types from SSE: server may emit Azure-style 'start', 'incomplete', 'done'
  // along with legacy 'chunk', 'message', 'complete', and 'error'.
  type:
    | 'start'
    | 'incomplete'
    | 'done'
    | 'chunk'
    | 'message'
    | 'complete'
    | 'error';
  // Content fields
  content?: string;
  partial_response?: string;
  full_response?: string;
  message?: string;
  // Context and telemetry
  context_items?: number;
  chunks?: number;
  request_id?: string;
  deployment?: string;
  api_version?: string;
  mode?: string;
  done?: boolean;
  // Error details
  code?: string;
  detail?: string;
  // Optional truncated RAG context snapshot
  rag_context?: Partial<RAGContext>;
}

/**
 * Service for managing AI chatbot interactions with streaming capabilities.
 *
 * Handles real-time chat messaging, streaming responses from the ChatBot RAG API,
 * typing effects, user/company context management, and error handling with
 * support ticket integration.
 *
 * Features:
 * - Streaming chat responses with typing animation
 * - Multi-tenant context headers for secure API access
 * - Comprehensive error handling with user-friendly messages
 * - Message state management using RxJS observables
 * - Automatic cleanup of resources and timers
 *
 * @example
 * ```typescript
 * constructor(private chatBotService: ChatBotService) {}
 *
 * ngOnInit() {
 *   this.chatBotService.messages$.subscribe(messages => {
 *     this.messages = messages;
 *   });
 * }
 *
 * sendMessage(query: string) {
 *   this.chatBotService.sendStreamingQuery(query);
 * }
 *
 * ngOnDestroy() {
 *   this.chatBotService.cleanup();
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class ChatBotService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private loggingService = inject(LoggingService);
  private apiConfigService = inject(ApiConfigurationService);
  private companyService = inject(CompanyService);
  private pageContextService = inject(PageContextService);

  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  private streamingSubject = new Subject<StreamResponse>();
  private isStreamingSubject = new BehaviorSubject<boolean>(false);
  private ragContextSubject = new BehaviorSubject<Partial<RAGContext> | null>(null);

  public messages$ = this.messagesSubject.asObservable();
  public streaming$ = this.streamingSubject.asObservable();
  public isStreaming$ = this.isStreamingSubject.asObservable();
  public ragContext$ = this.ragContextSubject.asObservable();

  private typingBuffer = '';
  private typingTimer: any = null;
  private typingBatchSize = 3; // chars per tick
  private typingIntervalMs = 18; // fast typing speed
  private pendingCompletion: { contextItems?: number } | null = null;
  private abortController: AbortController | null = null;

  constructor() {
    this.initializeChat();
  }

  /**
   * Initialize chat with welcome message
   */
  private initializeChat(): void {
    const welcomeMessage: ChatMessage = {
      id: this.generateMessageId(),
      type: 'system',
      content:
        'Welcome to ScanNinja AI! I can help you analyze your security scans, understand vulnerabilities, provide remediation guidance, and guide you through the platform. Ask me about your latest scans, specific CVEs, or how to use features like starting scans, finding reports, or exporting data.',
      timestamp: new Date(),
    };
    this.messagesSubject.next([welcomeMessage]);
  }

  /**
   * Get company ID from current user
   */
  private async getcurrentCompanyId(): Promise<string> {
    try {
      const user = await firstValueFrom(this.authService.currentUser);
      return (user as any)?.currentCompanyId || '';
    } catch (error) {
      this.loggingService.logError('Failed to get company ID', error);
      return '';
    }
  }

  /**
   * Get the ChatBot API URL using the base function URL
   */
  private async getChatBotApiUrl(): Promise<string> {
    try {
      const config = await firstValueFrom(
        this.apiConfigService.loadConfiguration(),
      );
      return `${config.services.baseFunction}/api/ChatBotRAG`;
    } catch (error) {
      this.loggingService.logError('Failed to load API configuration', error);
      return '';
    }
  }

  /**
   * Get the function token for API authentication
   */
  private async getFunctionToken(): Promise<string> {
    try {
      const tokenResponse = await this.apiConfigService.getFunctionToken();
      return tokenResponse.value || '';
    } catch (error) {
      this.loggingService.logError('Failed to get function token', error);
      return '';
    }
  }

  /**
   * Gather enriched user + company context for outbound headers. All fields required.
   * Throws an error if any required value is missing so caller can surface a user-friendly message.
   */
  private async getUserAndCompanyContext(): Promise<{
    currentCompanyId: string;
    accountUuid: string;
    companyName: string;
    userId: string;
    userEmail: string;
  }> {
    let currentCompanyId = '';
    let accountUuid = '';
    let companyName = '';
    let userId = '';
    let userEmail = '';
    try {
      const user: any = await firstValueFrom(this.authService.currentUser);
      if (user) {
        currentCompanyId = user.currentCompanyId || user.currentCompanyId || '';
        userId = user.uid || '';
        userEmail = user.email || '';
        let company = this.companyService.company;
        if (!company && currentCompanyId) {
          company = (await this.companyService.getCompany(currentCompanyId)) as any;
        }
        if (company) {
          accountUuid = company.accountUuid || '';
          companyName = company.name || '';
        }
      }
    } catch (err) {
      this.loggingService.logError(
        'Failed to build user/company context for chatbot headers',
        err,
      );
    }

    const missing: string[] = [];
    if (!currentCompanyId) missing.push('currentCompanyId');
    if (!accountUuid) missing.push('accountUuid');
    if (!companyName) missing.push('companyName');
    if (!userId) missing.push('userId');
    if (!userEmail) missing.push('userEmail');

    if (missing.length) {
      const errMsg = `Missing required user/company context: ${missing.join(', ')}`;
      this.loggingService.logError(errMsg);
      throw new Error(errMsg);
    }
    return { currentCompanyId, accountUuid, companyName, userId, userEmail };
  }

  /**
   * Send streaming query using fetch API for better header support
   */
  async sendStreamingQuery(query: string): Promise<void> {
    let context;
    try {
      context = await this.getUserAndCompanyContext();
    } catch (e: any) {
      this.loggingService.logError(
        'Failed to get user/company context for chatbot:',
        e,
      );
      this.addErrorMessage(
        'User context unavailable for chatbot. Please refresh the page or re-authenticate.',
      );
      return Promise.resolve();
    }
    const { currentCompanyId, accountUuid, companyName, userId, userEmail } = context;

    // Get configuration
    const apiUrl = await this.getChatBotApiUrl();
    const functionKey = await this.getFunctionToken();

    if (!apiUrl || !functionKey) {
      this.loggingService.logError('Missing API configuration for chatbot');
      this.addErrorMessage(
        'Chatbot configuration is missing. Please contact support.',
      );
      return Promise.resolve();
    }

    // Add user message
    this.addMessage({
      id: this.generateMessageId(),
      type: 'user',
      content: query,
      timestamp: new Date(),
    });

    // Add bot message placeholder
    const botMessage: ChatMessage = {
      id: this.generateMessageId(),
      type: 'bot',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };
    this.addMessage(botMessage);

    this.isStreamingSubject.next(true);

    // Generate a unique request ID for correlation using cryptographically secure random
    // 6 bytes = 48 bits entropy, hex encoding = 12 chars
    const randomBytes = new Uint8Array(6);
    window.crypto.getRandomValues(randomBytes);
    const securePart = Array.from(randomBytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    const requestId = `req_${Date.now()}_${securePart}`;

    // Get page context to provide scan-specific awareness
    const pageContext = this.pageContextService.getContext();

    const headers = {
      'X-Account-ID': accountUuid, // Primary tenant key for multi-tenant isolation
      'X-Company-ID': currentCompanyId, // Legacy support (optional)
      'X-Company': companyName, // Human-readable company name
      'X-User-Id': userId, // Authenticated user UUID/ID
      'X-User-Email': userEmail, // User email for audit logging
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'Cache-Control': 'no-cache, no-transform',
      'X-Request-ID': requestId,
      'x-functions-key': functionKey,
    };

    // Add page context headers if available
    if (pageContext?.pageType) {
      (headers as any)['X-Page-Context'] = pageContext.pageType;
    }
    if (pageContext?.resourceId) {
      (headers as any)['X-Resource-ID'] = pageContext.resourceId;
    }
    if (pageContext?.resourceName) {
      (headers as any)['X-Resource-Name'] = pageContext.resourceName;
    }

    // this.loggingService.logInfo('Sending chatbot query', { query, currentCompanyId, requestId });

    let hadAnyChunks = false;
    let streamCompleted = false;

    // Create abort controller for this request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query, stream: true }),
      signal: this.abortController.signal,
    })
      .then((response) => {
        // Check for server errors first
        if (!response.ok) {
          if (response.status >= 500) {
            throw new Error(
              `Server error (${response.status}): The AI service is temporarily unavailable. Please try again.`,
            );
          } else if (response.status === 404) {
            throw new Error(
              `Service not found (${response.status}): The ChatBot service may not be deployed properly.`,
            );
          } else {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
        }

        if (!response.body) {
          throw new Error('No response body received from server');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        const readStream = (): Promise<void> => {
          return reader
            .read()
            .then(({ done, value }) => {
              if (done) {
                // If there is any remaining buffered line, attempt to process it once
                const leftover = buffer.trim();
                if (leftover) {
                  try {
                    let payloadStr: string | null = null;
                    if (leftover.startsWith('data:')) {
                      payloadStr = leftover.substring(5).trim();
                    } else if (/^message\b/i.test(leftover)) {
                      payloadStr = leftover.replace(/^message\s*/i, '').trim();
                    } else if (leftover.startsWith('{')) {
                      payloadStr = leftover;
                    }
                    if (payloadStr && payloadStr !== '[DONE]') {
                      const normalizedJson = payloadStr.replace(/'/g, '"');
                      const data: StreamResponse = JSON.parse(normalizedJson);
                      this.streamingSubject.next(data);
                      if (data.type === 'chunk' || data.type === 'message') {
                        const text = data.content ?? data.message;
                        if (text) {
                          hadAnyChunks = true;
                          this.enqueueBotText(text);
                        }
                      } else if (data.type === 'incomplete') {
                        if (typeof data.context_items === 'number') {
                          this.pendingCompletion = { contextItems: data.context_items };
                        }
                        const text = data.partial_response;
                        if (text) {
                          hadAnyChunks = true;
                          this.enqueueBotText(text);
                        }
                      } else if (data.type === 'done' || data.type === 'complete') {
                        streamCompleted = true;
                        if (this.typingBuffer.length) {
                          this.pendingCompletion = {
                            contextItems: data.context_items,
                          };
                        } else {
                          // Only set hadAnyChunks if we actually have a full_response
                          if (data.full_response) {
                            hadAnyChunks = true;
                          }
                          this.completeLastBotMessage(
                            data.full_response,
                            data.context_items,
                          );
                        }
                        if (data.rag_context) this.ragContextSubject.next(data.rag_context);
                      }
                    }
                  } catch (e) {
                    this.loggingService.logError('Error parsing leftover stream data', {
                      error: e,
                      errorMessage: e instanceof Error ? e.message : String(e),
                      errorStack: e instanceof Error ? e.stack : undefined,
                      leftover,
                      requestId
                    });
                  }
                }

                // Stream ended - check if we had any content
                if (!streamCompleted && !hadAnyChunks) {
                  this.updateLastBotMessage(
                    'No response received from AI service. This may be due to a configuration issue. Please try again or contact support.',
                  );
                } else if (!streamCompleted) {
                  // Stream ended without proper completion event
                  this.completeLastBotMessage();
                }
                this.isStreamingSubject.next(false);
                return Promise.resolve();
              }

              // Accumulate and process full lines only; keep remainder
              buffer += decoder.decode(value, { stream: true });

              let newlineIndex = buffer.indexOf('\n');
              while (newlineIndex !== -1) {
                const rawLine = buffer.slice(0, newlineIndex).replace(/\r$/, '');
                buffer = buffer.slice(newlineIndex + 1);

                const line = rawLine.trim();
                if (!line) {
                  newlineIndex = buffer.indexOf('\n');
                  continue;
                }

                let payloadStr: string | null = null;
                if (line.startsWith('data:')) {
                  payloadStr = line.substring(5).trim();
                } else if (/^message\b/i.test(line)) {
                  payloadStr = line.replace(/^message\s*/i, '').trim();
                } else if (line.startsWith('{')) {
                  payloadStr = line; // bare JSON line
                } else if (/^event:/i.test(line)) {
                  // ignore event name lines; expect following data line
                  newlineIndex = buffer.indexOf('\n');
                  continue;
                }

                if (!payloadStr || payloadStr === '[DONE]') {
                  newlineIndex = buffer.indexOf('\n');
                  continue;
                }

                try {
                  const normalizedJson = payloadStr.replace(/'/g, '"');
                  const data: StreamResponse = JSON.parse(normalizedJson);

                  this.streamingSubject.next(data);

                  switch (data.type) {
                    case 'start': {
                      // Start event doesn't contain actual content, just a status message
                      this.updateLastBotMessage(
                        data.message ||
                          'ScanNinja AI is analyzing your security data...',
                      );
                      break;
                    }
                    case 'incomplete': {
                      // Progress update; optionally includes partial_response and context_items
                      if (typeof data.context_items === 'number') {
                        // Capture to apply on completion
                        this.pendingCompletion = {
                          contextItems: data.context_items,
                        };
                      }
                      const text = data.partial_response;
                      if (text) {
                        hadAnyChunks = true;
                        this.enqueueBotText(text);
                      }
                      break;
                    }
                    case 'done': {
                      // Stream finished (Azure-style)
                      streamCompleted = true;
                      // Finalize any pending typing; apply last known context_items
                      const ctx = this.pendingCompletion?.contextItems;
                      if (this.typingBuffer.length) {
                        this.pendingCompletion = { contextItems: ctx };
                      } else {
                        this.completeLastBotMessage(undefined, ctx);
                        this.pendingCompletion = null;
                      }
                      this.isStreamingSubject.next(false);
                      break;
                    }
                    case 'chunk':
                    case 'message': {
                      const text = data.content ?? data.message;
                      if (text) {
                        hadAnyChunks = true;
                        this.enqueueBotText(text);
                      }
                      break;
                    }
                    case 'complete': {
                      streamCompleted = true;
                      if (this.typingBuffer.length) {
                        this.pendingCompletion = {
                          contextItems: data.context_items,
                        };
                      } else {
                        // Only set hadAnyChunks if we have actual content
                        if (data.full_response) {
                          hadAnyChunks = true;
                        }
                        this.completeLastBotMessage(
                          data.full_response,
                          data.context_items,
                        );
                      }
                      if (data.rag_context) {
                        this.loggingService.logInfo(
                          'Received truncated rag_context snapshot',
                          {
                            scans: data.rag_context.scans?.length,
                            scan_details: data.rag_context.scan_details?.length,
                            vulnerabilities: data.rag_context.vulnerabilities?.length,
                            cve_docs: data.rag_context.cve_docs?.length,
                            tickets: data.rag_context.tickets?.length,
                            active_scans: data.rag_context.active_scans?.length,
                          },
                        );
                        this.ragContextSubject.next(data.rag_context);
                      }
                      this.isStreamingSubject.next(false);
                      break;
                    }
                    case 'error': {
                      streamCompleted = true;
                      let errorMsg = data.message || 'Unknown error occurred';
                      if (data.detail) errorMsg += `\nDetail: ${data.detail}`;
                      if (data.code) errorMsg += `\n(Code: ${data.code})`;
                      this.loggingService.logError('Server-side streaming error', {
                        error: errorMsg,
                        code: data.code,
                        detail: data.detail,
                        requestId,
                      });

                      let userFriendlyError = errorMsg;
                      if (
                        errorMsg.includes('DeploymentNotFound') ||
                        errorMsg.includes('deployment')
                      ) {
                        userFriendlyError =
                          'AI service configuration issue detected. Please contact your administrator.';
                      } else if (
                        errorMsg.includes('quota') ||
                        errorMsg.includes('rate limit')
                      ) {
                        userFriendlyError =
                          'AI service is currently at capacity. Please try again in a few minutes.';
                      }

                      const ticketBaseUrl = '/dashboard/support/create';
                      const ticketMessage = encodeURIComponent(
                        `ChatBot Error Report\n\nMessage: ${data.message || ''}\nDetail: ${data.detail || ''}\nCode: ${data.code || ''}\nRequest ID: ${requestId}\n\nPlease describe what you were doing when this error occurred:\n`,
                      );
                      const ticketUrl = `${ticketBaseUrl}?message=${ticketMessage}`;
                      // Escape single quotes in URL to prevent XSS in onclick handler
                      const escapedUrl = ticketUrl.replace(/'/g, '&#39;');
                      const supportLink = `<div class='mt-3'><button class='btn btn-sm btn-outline-warning' onclick="window.dispatchEvent(new CustomEvent('sn-create-support-ticket', { detail: { url: '${escapedUrl}' } }))">Create Support Ticket</button></div>`;

                      this.updateLastBotMessage(`Error: ${userFriendlyError}${supportLink}`);
                      this.isStreamingSubject.next(false);
                      break;
                    }
                  }
                } catch (parseError) {
                  this.loggingService.logError('Error parsing stream chunk', {
                    error: parseError,
                    errorMessage: parseError instanceof Error ? parseError.message : String(parseError),
                    errorStack: parseError instanceof Error ? parseError.stack : undefined,
                    line,
                    requestId,
                  });
                }

                newlineIndex = buffer.indexOf('\n');
              }

              return readStream();
            })
            .catch((streamError) => {
              this.loggingService.logError('Stream reading error', {
                error: streamError,
                requestId,
                hadAnyChunks,
              });

              if (!hadAnyChunks) {
                this.updateLastBotMessage(
                  'Connection interrupted before receiving response. Please check your network and try again.',
                );
              } else {
                this.updateLastBotMessage(
                  (this.getMessages()[this.getMessages().length - 1]?.content ||
                    '') + '\n\n[Connection interrupted]',
                );
              }
              this.isStreamingSubject.next(false);
              return Promise.resolve();
            });
        };

        return readStream();
      })
      .catch((fetchError) => {
        // Check if the error is due to abort
        if (fetchError.name === 'AbortError') {
          this.updateLastBotMessage(
            (this.getMessages()[this.getMessages().length - 1]?.content || '') +
            '\n\n[Response stopped by user]'
          );
          this.isStreamingSubject.next(false);
          return Promise.resolve();
        }

        this.loggingService.logError('Chatbot API fetch error', {
          error: fetchError,
          requestId,
          apiUrl: apiUrl.replace(/\?.*$/, ''), // Log URL without sensitive params
        });

        let errorMessage = fetchError.message;
        if (
          fetchError.name === 'TypeError' &&
          fetchError.message.includes('fetch')
        ) {
          errorMessage =
            'Network connection failed. Please check your internet connection and try again.';
        }

        // Build a support ticket link with error details prefilled
        const ticketBaseUrl = '/dashboard/support/create';
        const ticketMessage = encodeURIComponent(
          `ChatBot Error Report\n\nMessage: ${errorMessage}\nRequest ID: ${requestId}\n\nPlease describe what you were doing when this error occurred:`,
        );
        const ticketUrl = `${ticketBaseUrl}?message=${ticketMessage}`;
        const supportLink = `<div class='mt-3'><a class='btn btn-sm btn-outline-warning' href='${ticketUrl}' target='_blank' rel='noopener'>Create Support Ticket</a></div>`;

        this.updateLastBotMessage(
          `Connection error: ${errorMessage}${supportLink}`,
        );
        this.isStreamingSubject.next(false);
        return Promise.resolve();
      });
  }

  /**
   * Send non-streaming query (fallback method)
   */
  async sendQuery(query: string): Promise<Observable<any>> {
    let context;
    try {
      context = await this.getUserAndCompanyContext();
    } catch (e: any) {
      this.loggingService.logError(
        'Failed to get user/company context for chatbot:',
        e,
      );
      throw new Error('User context unavailable for chatbot request');
    }
    const { currentCompanyId, accountUuid, companyName, userId, userEmail } = context;

    // Get configuration
    const apiUrl = await this.getChatBotApiUrl();
    const functionKey = await this.getFunctionToken();

    if (!apiUrl || !functionKey) {
      throw new Error('Missing API configuration for chatbot');
    }
    const headers = new HttpHeaders({
      'X-Account-ID': accountUuid, // Primary tenant key for multi-tenant isolation
      'X-Company-ID': currentCompanyId, // Legacy support (optional)
      'X-Company': companyName, // Human-readable company name
      'X-User-Id': userId, // Authenticated user UUID/ID
      'X-User-Email': userEmail, // User email for audit logging
      'Content-Type': 'application/json',
      'x-functions-key': functionKey,
    });

    const body = { query };

    return this.http.post(apiUrl, body, { headers }).pipe(take(1));
  }

  /**
   * Convenience method: perform non-streaming request and push answer + rag_context to observables.
   */
  async sendQueryAndStore(query: string): Promise<void> {
    try {
      const response$: Observable<any> = await this.sendQuery(query);
      const resp = await firstValueFrom(response$);
      // Expect shape: { data: { answer, context_items, rag_context }, message }
      const data = resp?.data || resp; // fallback if API wraps differently
      if (data?.rag_context) {
        this.ragContextSubject.next(data.rag_context);
      }
      if (data?.answer) {
        this.addMessage({
          id: this.generateMessageId(),
          type: 'user',
          content: query,
          timestamp: new Date(),
        });
        this.addMessage({
          id: this.generateMessageId(),
          type: 'bot',
          content: data.answer,
          timestamp: new Date(),
          contextItems: data.context_items,
        });
      }
    } catch (err) {
      this.addErrorMessage('Error fetching chatbot response (non-stream).');
      this.loggingService.logError('Non-stream chatbot error', err);
    }
  }

  /**
   * Stop any ongoing streaming request
   */
  stopStreaming(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // Stop typing animation
    if (this.typingTimer) {
      clearInterval(this.typingTimer);
      this.typingTimer = null;
    }

    // Clear buffer
    this.typingBuffer = '';

    // Complete any pending message
    if (this.pendingCompletion) {
      this.completeLastBotMessage(
        undefined,
        this.pendingCompletion.contextItems,
      );
      this.pendingCompletion = null;
    }

    // Update streaming state
    this.isStreamingSubject.next(false);
  }

  /**
   * Clear all messages and reinitialize
   */
  clearMessages(): void {
    this.stopStreaming();
    this.initializeChat();
  }

  /**
   * Reset session state (for when user closes chat window)
   */
  resetSession(): void {
    this.stopStreaming();
    this.ragContextSubject.next(null);
    this.clearMessages();
  }

  /**
   * Get current messages
   */
  getMessages(): ChatMessage[] {
    return this.messagesSubject.value;
  }

  private addMessage(message: ChatMessage): void {
    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next([...currentMessages, message]);
  }

  private addErrorMessage(content: string): void {
    const errorMessage: ChatMessage = {
      id: this.generateMessageId(),
      type: 'system',
      content,
      timestamp: new Date(),
    };
    this.addMessage(errorMessage);
  }

  private updateLastBotMessage(content: string): void {
    const messages = this.messagesSubject.value;
    const lastMessage = messages[messages.length - 1];

    if (lastMessage && lastMessage.type === 'bot') {
      lastMessage.content = content;
      this.messagesSubject.next([...messages]);
    }
  }

  private appendToLastBotMessage(content: string): void {
    const messages = this.messagesSubject.value;
    const lastMessage = messages[messages.length - 1];

    if (lastMessage && lastMessage.type === 'bot') {
      lastMessage.content += content;
      this.messagesSubject.next([...messages]);
    }
  }

  private completeLastBotMessage(
    finalContent?: string,
    contextItems?: number,
  ): void {
    const messages = this.messagesSubject.value;
    const lastMessage = messages[messages.length - 1];

    if (lastMessage && lastMessage.type === 'bot') {
      if (finalContent) {
        lastMessage.content = finalContent;
      }
      if (contextItems !== undefined) {
        lastMessage.contextItems = contextItems;
      }
      lastMessage.isStreaming = false;
      this.messagesSubject.next([...messages]);
    }
  }

  /**
   * Stop any active typing interval and flush pending completion.
   * Should be called when the hosting component is destroyed to avoid orphan timers.
   */
  public cleanup(): void {
    if (this.typingTimer) {
      clearInterval(this.typingTimer);
      this.typingTimer = null;
    }
    this.typingBuffer = '';
    if (this.pendingCompletion) {
      // finalize any streaming message
      this.completeLastBotMessage(
        undefined,
        this.pendingCompletion.contextItems,
      );
      this.pendingCompletion = null;
      this.isStreamingSubject.next(false);
    }
  }

  private generateMessageId(): string {
    // Use cryptographically secure random values for message IDs
    // 6 bytes = 48 bits entropy, hex encoding = 12 chars
    const randomBytes = new Uint8Array(6);
    window.crypto.getRandomValues(randomBytes);
    const securePart = Array.from(randomBytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    return `msg_${Date.now()}_${securePart}`;
  }

  private startTypingTimer(): void {
    if (this.typingTimer) return;
    this.typingTimer = setInterval(() => {
      if (!this.typingBuffer.length) {
        clearInterval(this.typingTimer);
        this.typingTimer = null;
        // If completion already signaled and no buffer left, finalize
        if (this.pendingCompletion) {
          this.completeLastBotMessage(
            undefined,
            this.pendingCompletion.contextItems,
          );
          this.pendingCompletion = null;
        }
        return;
      }
      const slice = this.typingBuffer.slice(0, this.typingBatchSize);
      this.typingBuffer = this.typingBuffer.slice(this.typingBatchSize);
      this.appendToLastBotMessage(slice);
    }, this.typingIntervalMs);
  }

  private enqueueBotText(content: string): void {
    if (!content) return;
    this.typingBuffer += content;
    this.startTypingTimer();
  }
}
